/** "Make available offline", on the Settings screen.
 *
 * Lives in the app rather than in `@game/ui` because it talks to a service
 * worker, which is a property of how the game is HOSTED and not of the kit.
 * `Settings` takes it through its `extra` slot.
 *
 * The card is honest about the common case: on this deploy the whole feature
 * is unavailable, because a service worker is only given to a secure context
 * and the game is served over plain http. Saying that plainly is the point -
 * a greyed button with no reason is the thing a player files a bug about.
 */

import { useCallback, useEffect, useState } from "react";
import {
  downloadOffline, offlineSize, offlineStatus, type OfflineState,
} from "./offline.js";

const mb = (n: number): string => `${(n / 1048576).toFixed(0)} MB`;

export function OfflineCard(): JSX.Element {
  const [state, setState] = useState<OfflineState>(() => offlineStatus());
  const [counts, setCounts] = useState<{ bulk: number; music: number } | null>(null);
  const [withMusic, setWithMusic] = useState(false);

  useEffect(() => {
    let live = true;
    void offlineSize().then((s) => { if (live && s !== null) setCounts(s); });
    return () => { live = false; };
  }, []);

  const start = useCallback(() => {
    setState({ kind: "downloading", done: 0, total: 0, bytes: 0 });
    downloadOffline({ music: withMusic }, setState);
  }, [withMusic]);

  if (state.kind === "unsupported") {
    return (
      <div className="hs-card hs-wood">
        <div className="hs-card__title">Play offline</div>
        <div className="hs-tiny hs-muted" style={{ lineHeight: 1.5 }}>
          {state.why === "insecure"
            ? "Not available over a plain http:// address. Browsers only hand the "
              + "offline cache to a site served over https, so this needs the game "
              + "to be on an https host before it can store anything. Your saved "
              + "games are unaffected - those live in this browser either way."
            : "This browser does not support offline caching."}
        </div>
      </div>
    );
  }

  const busy = state.kind === "downloading";
  return (
    <div className="hs-card hs-wood">
      <div className="hs-card__title">Play offline</div>
      <div className="hs-tiny hs-muted" style={{ lineHeight: 1.5, marginBottom: "8px" }}>
        {"The game already keeps what you have played. This fetches the rest of the "
         + "art up front, so it runs with no connection at all."}
        {counts === null ? "" : ` ${String(counts.bulk)} art files`}
        {counts === null || !withMusic ? "" : ` and ${String(counts.music)} music tracks`}
        {counts === null ? "" : "."}
      </div>

      <label className="hs-tiny" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <input
          type="checkbox"
          checked={withMusic}
          disabled={busy}
          onChange={(e) => { setWithMusic(e.currentTarget.checked); }}
        />
        {/* Named separately because it is most of the download and none of the
            game: a player on a metered connection should be able to say no. */}
        Include the music (about 47 MB more)
      </label>

      <button
        type="button"
        className="hs-plate hs-plate--sm"
        disabled={busy}
        onClick={start}
      >
        {busy ? "Downloading…" : state.kind === "cached" ? "Download again" : "Make available offline"}
      </button>

      {state.kind === "downloading" ? (
        <div className="hs-tiny hs-muted" style={{ marginTop: "8px" }}>
          {state.total === 0
            ? "Starting…"
            : `${String(state.done)} of ${String(state.total)} — ${mb(state.bytes)}`}
        </div>
      ) : null}
      {state.kind === "cached" ? (
        <div className="hs-tiny" style={{ marginTop: "8px", color: "#c9e8a8" }}>
          {`Stored ${String(state.done)} files, ${mb(state.bytes)}. The game will now open without a connection.`}
        </div>
      ) : null}
    </div>
  );
}
